/**
 * 消息列表管理 composable。
 *
 * <p>职责：</p>
 * <ul>
 *   <li>持有 {@link messages} ref 与 per-session 快照缓存 {@link messagesBySession}</li>
 *   <li>watch messages → 自动同步当前 session 的快照到缓存</li>
 *   <li>提供 {@link hasMessages} 派生状态</li>
 *   <li>提供 {@link appendAssistantMessage} 追加助手消息</li>
 *   <li>提供 {@link reconcileStaleStreamMessages} 切换会话时清理 stale pending</li>
 * </ul>
 *
 * <p>本 composable 仅依赖 sessionId ref（用于缓存键），不依赖其他子 composable。</p>
 */
import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'
import { hasVisibleAssistantTurn } from './messages'
import { genMessageId, type AiUiMessage } from './types'

export function useMessages(sessionId: Ref<string | null>): {
  messages: Ref<AiUiMessage[]>
  messagesBySession: Map<string, AiUiMessage[]>
  hasMessages: ComputedRef<boolean>
  appendAssistantMessage: (content: string) => void
  reconcileStaleStreamMessages: (targetSessionId: string) => void
} {
  const messages = ref<AiUiMessage[]>([])

  /**
   * Per-session 消息快照缓存。
   *
   * 用于在用户跨会话切换时保留本地视图：
   * 切走时（attachToSession 把 sessionId 改成新值）自动快照旧会话；
   * 切回时若缓存命中直接还原，避免被服务端尚未落库的"半成品"回复覆盖导致页面空白。
   */
  const messagesBySession = new Map<string, AiUiMessage[]>()

  /**
   * 同步 messages 变更到当前 sessionId 对应的缓存。
   *
   * 性能债修复(热点1)：只监听数组身份，不再 deep 遍历整个消息树——
   * 流式期每个 token 的深层属性赋值曾触发数次全图深度遍历，是长会话卡顿的主因。
   * 缓存新鲜度改由两个显式机制保障：
   *  1. sessionId 变更时（同步拍）把切换前一刻的消息存入旧会话缓存；
   *  2. attachToSession 在读取缓存前主动保存当前会话（读点保鲜）。
   * 历史伤疤（旧会话深层变更写进新会话缓存）不会复发：sessionId 监听在切换瞬间
   * 以旧 sid + 当时的 messages 落存，不依赖可能排队到切换之后的深层变更回调。
   */
  watch(
    () => messages.value,
    (next) => {
      const sid = sessionId.value
      if (sid) {
        messagesBySession.set(sid, [...next])
      }
    },
    { flush: 'sync' }
  )

  watch(
    sessionId,
    (next, prev) => {
      if (prev && prev !== next) {
        // 切换瞬间 messages 仍指向旧会话内容（attach 随后才覆写），此刻落存旧会话快照。
        messagesBySession.set(prev, [...messages.value])
      }
    },
    { flush: 'sync' }
  )

  const hasMessages = computed(() => messages.value.length > 0)

  function appendAssistantMessage(content: string): void {
    if (!content.trim()) return
    messages.value = [
      ...messages.value,
      {
        id: genMessageId(),
        role: 'assistant',
        content,
        createdAt: Date.now()
      }
    ]
  }

  function reconcileStaleStreamMessages(targetSessionId: string): void {
    messages.value = messages.value
      .map((m) => {
        if (m.role !== 'assistant' || !m.pending) {
          return m
        }
        return {
          ...m,
          pending: false
        }
      })
      .filter((m) => m.role !== 'assistant' || hasVisibleAssistantTurn(m))
    messagesBySession.set(targetSessionId, [...messages.value])
  }

  return {
    messages,
    messagesBySession,
    hasMessages,
    appendAssistantMessage,
    reconcileStaleStreamMessages
  }
}
